import { all, call, delay, put, select, takeLatest } from 'redux-saga/effects';
import { formatUnits, Provider, Signer } from 'ethers';
import { Errors } from '@blink/components/src/constants/errors';
import Logger from '@blink/components/src/utils/logger';

import { getWalletInfoSaga } from './common';

import { Store } from '@/shared/store';
import {
    clearErrorsAction,
    userMarginAccountsErrorAction,
    usersVaultsContractNotAvailableAction,
    usersVaultsErrorAction,
    usersVaultsPendingRewardsErrorAction,
} from '@/shared/store/errors/actions';
import { GET_MARGIN_ACCOUNTS, GET_USER_VAULTS } from '@/shared/store/userVaults/constants';
import { UserVault } from '@/shared/store/userVaults';
import { setUserMarginAccounts, setUserVaults } from '@/shared/store/userVaults/actions';
import AgreementABI from '@/shared/abi/AgreementABI.json';
import { getMarginAccounts, MarginAccount, ROLE, Vault } from '@/shared/api/vault';
import { getSafeContract } from '@/shared/utils/getSafeContract';
import {
    MARGIN_ACCOUNTS_ERROR,
    USER_VAULTS_ERROR,
    USERS_VAULTS_CONTRACT_NOT_AVAILABLE_ERROR,
    USERS_VAULTS_PENDING_REWARDS_ERROR,
} from '@/shared/store/errors/constants';

const PULL_INTERVAL = 10000;

/**
 *  Clear error state
 */
function* clearUserVaultsErrors() {
    yield all([
        put(clearErrorsAction({ errorType: USER_VAULTS_ERROR })),
        put(clearErrorsAction({ errorType: USERS_VAULTS_CONTRACT_NOT_AVAILABLE_ERROR })),
        put(clearErrorsAction({ errorType: USERS_VAULTS_PENDING_REWARDS_ERROR })),
    ]);
}

/**
 * Get vaults balance
 */
function* fetchUserVaultsBalance(
    vaults: Vault[],
    walletAddress: string,
    signer: Signer | Provider,
): Generator<any, UserVault[], any> {
    const userVaults: UserVault[] = yield all(
        vaults
            .filter((v) => v.address && v.role === ROLE.INVESTOR)
            .map((v) =>
                call(function* (): Generator<any, UserVault | null, any> {
                    try {
                        const vault = getSafeContract(v.address, AgreementABI, signer);
                        const balance: bigint = yield vault.balanceOf(walletAddress);

                        if (balance !== 0n) {
                            return {
                                address: v.address,
                                title: v.title,
                                totalDeposited: v.totalDeposited,
                                totalDepositThreshold: v.totalDepositThreshold,
                                balance: formatUnits(balance, v.leverage.decimals),
                                reward: 'N/A',
                                apy: v.apy,
                                tokenAddress: v.leverage.address,
                                tokenDecimals: v.leverage.decimals,
                                tokenSymbol: v.leverage.symbol,
                                curator: v.curator,
                                netApy: v.netApy,
                            };
                        }
                    } catch (error) {
                        Logger.error('Error in userVaults::vault:balanceOf', { error });

                        yield put(
                            usersVaultsContractNotAvailableAction({
                                message: Errors.USERS_VAULTS_CONTRACT_NOT_AVAILABLE + ' ' + v.title,
                            }),
                        );
                    }
                    return null;
                }),
            ),
    );

    return userVaults.filter((vault): vault is UserVault => vault !== null);
}

/**
 *  Rewards refresh
 */
function* fetchUserVaultsPendingRewardsLoop(
    vaults: Vault[],
    walletAddress: string,
    signer: Signer | Provider,
    userVaults: UserVault[],
) {
    while (userVaults.length) {
        yield all(
            vaults
                .filter((v) => v.address)
                .map((v) =>
                    call(function* () {
                        try {
                            const vault = getSafeContract(v.address, AgreementABI, signer);
                            const reward: bigint = yield vault.pendingRewards(walletAddress);

                            const interestAccrued = formatUnits(reward, v.leverage.decimals);
                            v.interestAccrued = interestAccrued;

                            const userVault = userVaults.find((uv) => uv.address === v.address);
                            if (userVault) {
                                userVault.reward = interestAccrued;
                            }
                        } catch (error) {
                            Logger.error('Error in userVaults::vault:pendingRewards', { error });

                            yield put(
                                usersVaultsPendingRewardsErrorAction({
                                    message:
                                        Errors.USERS_VAULTS_PENDING_REWARDS_ERROR + ' ' + v.title,
                                }),
                            );
                        }
                    }),
                ),
        );

        yield put(setUserVaults([...userVaults]));

        yield delay(PULL_INTERVAL);
    }
}

/**
 * Main saga
 */
export function* getUserVaultsSaga(): Generator<any, any, any> {
    try {
        yield call(clearUserVaultsErrors);

        const { vaults } = yield select((state: Store) => ({
            vaults: state.vaults.vaults,
        }));

        const walletData = yield call(getWalletInfoSaga, () => setUserVaults([]));
        if (!walletData) return;
        const { walletAddress, signer } = walletData;

        const userVaults = yield call(fetchUserVaultsBalance, vaults, walletAddress, signer);
        yield put(setUserVaults(userVaults));

        yield call(fetchUserVaultsPendingRewardsLoop, vaults, walletAddress, signer, userVaults);
    } catch (e: any) {
        yield put(setUserVaults([]));
        yield put(usersVaultsErrorAction({ message: Errors.USERS_VAULTS + ' ' + e.message }));
        Logger.error('Error in userVaults:getUserVaultsSaga:', { error: e });
    }
}

function* getMarginAccountsSaga(action: {
    type: string;
    payload: {
        address: string;
        withoutCache: boolean;
    };
}): Generator<any, void, any> {
    try {
        const { address, withoutCache = false } = action.payload;

        yield put(clearErrorsAction({ errorType: MARGIN_ACCOUNTS_ERROR }));

        if (!address) {
            throw new Error(Errors.INVALID_WALLET_ADDRESS);
        }

        const marginAccounts: MarginAccount[] = yield call(
            getMarginAccounts,
            address,
            withoutCache,
        );

        if (!marginAccounts || marginAccounts.length === 0) {
            Logger.warn('No margin accounts found for address:', { address });
        }

        yield put(setUserMarginAccounts(marginAccounts));
    } catch (e: any) {
        Logger.error('Error in userVaults:getMarginAccountsSaga:', { error: e });

        yield put(
            userMarginAccountsErrorAction({
                message: Errors.MARGIN_ACCOUNTS + ' ' + e.message,
            }),
        );
    }
}

export function* userVaultsWatcher() {
    yield takeLatest(GET_USER_VAULTS, getUserVaultsSaga);
    yield takeLatest(GET_MARGIN_ACCOUNTS, getMarginAccountsSaga);
}
